<template>
  <article class="ally-card" :data-ally-id="String(ally?.id || '')">
    <figure class="ally-card__figure">
      <img
        :src="coverImage"
        :alt="ally?.name || ''"
        class="ally-card__image"
        loading="lazy"
      />
      <span
        v-if="!inForce"
        class="ally-card__badge"
      >
        {{ t("allies.badge_out_of_force") }}
      </span>
    </figure>

    <div class="ally-card__content">
      <header class="ally-card__header">
        <h3 class="ally-card__title">{{ ally?.name }}</h3>
        <p v-if="categoryLabel" class="ally-card__category">
          {{ categoryLabel }}
        </p>
      </header>

      <footer class="ally-card__footer">
        <button
          type="button"
          class="ally-card__button ally-card__button--primary"
          @click="emit('more-info', ally)"
        >
          {{ t("allies.button_more_info") }}
        </button>
        <button
          type="button"
          class="ally-card__button ally-card__button--ghost"
          @click="emit('share', ally)"
          :aria-label="t('allies.button_share_aria', { name: ally?.name || '' })"
        >
          {{ t("allies.button_share") }}
        </button>
      </footer>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { isAllyInForce } from "@/composables/useAllies";

const emit = defineEmits(["more-info", "share"]);

const props = defineProps({
  ally: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const coverImage = computed(() => {
  const fallback = "/default-image.jpg";
  const galleryImage = props.ally?.gallery?.[0]?.url || props.ally?.gallery?.[0]?.path;
  const imageList = props.ally?.images || props.ally?.gallery || [];
  const firstImage = imageList?.[0]?.url || imageList?.[0]?.path;
  return galleryImage || firstImage || props.ally?.cover_image || fallback;
});

const categoryLabel = computed(() => {
  const category = props.ally?.category || props.ally?.categories?.[0];
  const subCategory = props.ally?.sub_category || props.ally?.subCategory;

  if (category && subCategory) {
    return `${category?.name || category?.label || ""} · ${subCategory?.name || subCategory?.label || ""}`;
  }

  if (category) {
    return category?.name || category?.label || category;
  }

  return "";
});

const inForce = computed(() => isAllyInForce(props.ally));
</script>

<style scoped>
.ally-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;
}

.ally-card:focus-within,
.ally-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
}

.ally-card__figure {
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
}

.ally-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ally-card__badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: rgba(239, 68, 68, 0.95);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ally-card__content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 16px;
  gap: 16px;
}

.ally-card__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ally-card__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.ally-card__category {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
}

.ally-card__footer {
  margin-top: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ally-card__button {
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid transparent;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.ally-card__button--primary {
  background: #f97316;
  color: #ffffff;
  border-color: #f97316;
}

.ally-card__button--primary:hover,
.ally-card__button--primary:focus-visible {
  background: #ea580c;
  border-color: #ea580c;
}

.ally-card__button--ghost {
  background: #ffffff;
  color: #f97316;
  border-color: #f97316;
}

.ally-card__button--ghost:hover,
.ally-card__button--ghost:focus-visible {
  background: #fff7ed;
}

@media (max-width: 640px) {
  .ally-card__footer {
    grid-template-columns: 1fr;
  }
}
</style>
